import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService } from '../services/api'
import { fingerprintService } from '../services/fingerprint'
import { wsService } from '../services/websocket'
import { analytics } from '../services/analytics'
import { logger } from '../utils/logger'
import type { User } from '../../../shared/src/types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => {
    return !!user.value && apiService.isAuthenticated()
  })

  const currentUser = computed(() => user.value)

  // Actions
  async function login(email: string, password: string) {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.login(email, password)
      user.value = response.user
      
      // Set logger admin status
      logger.setAdminStatus(response.user.isAdmin || false)
      
      // Track successful login
      analytics.setUserId(response.user.id)
      analytics.trackConversion('login')
      
      // Reconnect WebSocket with new auth token
      wsService.disconnect()
      
      // Wait a moment for disconnect to complete, then reconnect
      setTimeout(() => {
        wsService.connect()
        
        // Authenticate after connection
        if (user.value?.id) {
          setTimeout(() => {
            if (wsService.isConnected()) {
              wsService.emit('auth', { userId: user.value!.id })
            }
          }, 1000)
        }
      }, 500)
      
      return response
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Login failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function register(email: string, password: string, name: string) {
    loading.value = true
    error.value = null

    try {
      // Generate device fingerprint before registration
      const deviceFingerprint = await fingerprintService.getOrCreateFingerprint()
      logger.debug('Registering with device fingerprint:', deviceFingerprint)
      
      const response = await apiService.register(email, password, name, deviceFingerprint)
      user.value = response.user
      
      // Track successful registration
      analytics.setUserId(response.user.id)
      analytics.trackConversion('registration')
      
      return response
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Registration failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function loadCurrentUser() {
    if (!apiService.isAuthenticated()) {
      return
    }

    // Try to load from localStorage first
    const storedUser = apiService.getCurrentUserFromStorage()
    if (storedUser) {
      user.value = storedUser
      logger.setAdminStatus(storedUser.isAdmin || false)
    }

    // Then verify with server
    try {
      const currentUser = await apiService.getCurrentUser()
      user.value = currentUser
      logger.setAdminStatus(currentUser.isAdmin || false)
    } catch (err: any) {
      // Only logout if it's an auth error (401), not a network error
      if (err.response?.status === 401) {
        logger.info('Token expired or invalid, logging out')
        logout()
      } else {
        // Network errors - keep using cached user data
        logger.warn('Could not verify user with server, using cached data:', err.message)
      }
    }
  }

  function logout() {
    // Clear analytics user ID
    analytics.clearUserId()
    
    // Clear logger admin status
    logger.setAdminStatus(false)
    
    user.value = null
    error.value = null
    apiService.logout()
  }

  function clearError() {
    error.value = null
  }

  // Initialize user from storage on store creation
  async function initialize() {
    if (initialized.value) {
      return // Already initialized
    }
    
    initialized.value = true
    await loadCurrentUser()
  }

  return {
    // State
    user,
    loading,
    error,
    // Getters
    isAuthenticated,
    currentUser,
    // Actions
    login,
    register,
    logout,
    loadCurrentUser,
    clearError,
    initialize
  }
})
